import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Trash2, Check, AlertTriangle } from 'lucide-react';
import type { SourceTable } from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { getExportSettings, setExportTables } from '../lib/callables.js';

type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'ok' }
  | { status: 'error'; message: string };

export function ExportSettingsPage() {
  const { userRecord } = useAuth();

  const [tables, setTables] = useState<string[]>([]);
  const [available, setAvailable] = useState<SourceTable[]>([]);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ status: 'idle' });

  // Add-table combobox state.
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const comboRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getExportSettings()
      .then((r) => {
        setTables(r.data.tables);
        setAvailable(r.data.availableTables);
        setUpdatedBy(r.data.updatedBy);
      })
      .catch((err) =>
        setLoadError(err instanceof Error ? err.message : 'Failed to load export settings.'),
      )
      .finally(() => setLoading(false));
  }, []);

  const scopeByTable = useMemo(() => {
    const m = new Map<string, 'direct' | 'join' | 'none'>();
    for (const t of available) {
      m.set(t.name, t.hasAgencyId ? 'direct' : t.joinScopable ? 'join' : 'none');
    }
    return m;
  }, [available]);

  const included = useMemo(() => [...tables].sort(), [tables]);

  // Tables in the schema not already included, matching the typed query.
  const candidates = useMemo(() => {
    const includedSet = new Set(tables);
    const q = query.trim().toLowerCase();
    return available
      .filter((t) => !includedSet.has(t.name) && t.name.toLowerCase().includes(q))
      .slice(0, 50);
  }, [available, tables, query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  async function persist(next: string[]) {
    setSave({ status: 'saving' });
    try {
      const r = await setExportTables({ tables: next });
      setTables(r.data.tables);
      setSave({ status: 'ok' });
    } catch (err) {
      setSave({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to save.',
      });
    }
  }

  function addTable(name: string) {
    if (tables.includes(name)) return;
    const next = [...tables, name];
    setTables(next);
    setQuery('');
    setOpen(false);
    void persist(next);
  }

  function removeTable(name: string) {
    const next = tables.filter((t) => t !== name);
    setTables(next);
    void persist(next);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || candidates.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, candidates.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = candidates[highlight];
      if (pick) addTable(pick.name);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  if (!userRecord?.isAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-8 py-8">
        <p className="text-sm text-[#73726e]">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#37352f] tracking-tight">
          Export settings
        </h1>
        <p className="text-sm text-[#73726e] mt-1">
          Tables included in client data exports. Tables with an{' '}
          <code className="text-xs">agency_id</code> column are scoped directly; some
          tables without it are scoped via a join to a parent (e.g. through{' '}
          <code className="text-xs">project_id</code> or{' '}
          <code className="text-xs">interviews_id</code>). Tables that are neither are
          skipped at export time.
        </p>
        {updatedBy && (
          <p className="text-xs text-[#aeaaa4] mt-1">Last changed by {updatedBy}</p>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-[#aeaaa4]">Loading…</p>
      ) : loadError ? (
        <p className="text-sm text-[#e03e3e]">{loadError}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Add table combobox */}
          <div ref={comboRef} className="relative max-w-md">
            <label className="text-xs font-medium text-[#73726e]" htmlFor="add-table">
              Add a table
            </label>
            <div className="relative mt-1">
              <input
                id="add-table"
                autoComplete="off"
                className="w-full px-2.5 py-1.5 pr-8 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
                placeholder="Search existing table names…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                  setHighlight(0);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
              />
              <Plus size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#aeaaa4]" />
            </div>

            {open && (
              <ul className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded border border-[#e9e9e7] bg-white shadow-sm">
                {candidates.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-[#aeaaa4]">
                    No matching tables
                  </li>
                ) : (
                  candidates.map((t, i) => (
                    <li key={t.name}>
                      <button
                        type="button"
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => addTable(t.name)}
                        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                          i === highlight ? 'bg-[#eef4fd]' : ''
                        } text-[#37352f]`}
                      >
                        <span className="truncate font-mono text-xs">{t.name}</span>
                        {!t.hasAgencyId && (
                          <span
                            className={`shrink-0 text-[10px] ${
                              t.joinScopable ? 'text-[#2f855a]' : 'text-[#b5820b]'
                            }`}
                          >
                            {t.joinScopable ? 'join-scoped' : 'no agency_id'}
                          </span>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          {/* Save status */}
          <div className="h-4 text-xs">
            {save.status === 'saving' && <span className="text-[#aeaaa4]">Saving…</span>}
            {save.status === 'ok' && <span className="text-[#2383e2]">Saved</span>}
            {save.status === 'error' && (
              <span className="text-[#e03e3e]">{save.message}</span>
            )}
          </div>

          {/* Included tables */}
          <div className="rounded border border-[#eae5dd] overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-[#F6F4F2] border-b border-[#eae5dd]">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#73726e]">
                Included tables ({included.length})
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#73726e]">
                scope
              </span>
            </div>
            {included.length === 0 ? (
              <p className="px-3 py-4 text-sm text-[#aeaaa4]">No tables included.</p>
            ) : (
              <ul>
                {included.map((name) => {
                  const scope = scopeByTable.get(name);
                  return (
                    <li
                      key={name}
                      className="flex items-center justify-between gap-3 px-3 py-2 border-b border-[#f1ede6] last:border-b-0"
                    >
                      <span className="font-mono text-xs text-[#37352f] truncate">{name}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        {scope === undefined ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#aeaaa4]">
                            not in schema
                          </span>
                        ) : scope === 'direct' ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#2f855a]">
                            <Check size={12} /> agency_id
                          </span>
                        ) : scope === 'join' ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#2f855a]">
                            <Check size={12} /> join
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[11px] text-[#b5820b]">
                            <AlertTriangle size={12} /> skipped
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeTable(name)}
                          className="text-[#aeaaa4] hover:text-[#e03e3e] transition-colors"
                          aria-label={`Remove ${name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
